import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Suspense, useEffect, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { attachmentCompose, messageCompose, prepareAttachment, removeQuotedMessage, sendChatMessage } from 'flow/actions/chat-actions';
import EmojiSearch from 'flow/components/messages/EmojiSearch';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useAutosizeTextArea from 'flow/hooks/useAutosizeTextArea';
import usePremadeAnswers from 'flow/hooks/usePremadeAnswers';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey, selectOwnUserHasCapability } from 'flow/selectors/ExtensionSelectors';
import { Capabilities } from 'flow/utils/capability-utils';
import { ChatAttachments, createMentionDTOsFromText, getAttachmentTypeFromExtension, MAX_ALLOWED_ATTACHMENT_SIZE, replaceWhitespaces } from 'flow/utils/message-utils';
import { getInitialPremadeAnswerInserterState, getNextPremadeAnswerInserterState, offerKeyEventToPremadeAnswerInserter } from 'flow/utils/premade-answer-utils';
import { isCmdOrCtrlPressed } from 'flow/utils/shortcut-utils';
import { twMerge } from 'tailwind-merge';
import AddButton from './AddButton';
import AttachmentPreview from './AttachmentPreview';
import EmojiPicker from './EmojiPicker';
import { getInitialMentionInserterState, getNextMentionInserterState, MentionInserter, offerKeyEventToMessageInserter } from './MentionInserter';
import { PremadeAnswerInserter } from './PremadeAnswersInserter';
import PremadeAnswersManager from './PremadeAnswersManager';
import QuotedMessage from './QuotedMessage';
import SendButton from './SendButton';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export var roomTypes = {
  session: 'session',
  room: 'room',
  "public": 'public'
};
// One source for the hint and the name, so the two cannot drift. A screen
// reader can list a page's fields on their own, away from the thread they sit
// in, where a bare "Message" says nothing about where the text is going -- the
// same reason a row's menu is named after its row.
//
// The placeholder stays absent until the session has a name, as it was; the
// name falls back to the bare word instead, since no name at all is worse
// than one without the subject.
var getComposerLabels = function getComposerLabels(threadMessageKey, sessionName, i18next) {
  if (threadMessageKey) {
    var replyToThread = i18next.t('ThreadMessages.ReplyThread');
    return {
      placeholder: replyToThread,
      ariaLabel: replyToThread
    };
  }
  var sendTo = i18next.t('message.input.send_to');
  if (!sessionName) {
    return {
      placeholder: undefined,
      ariaLabel: sendTo
    };
  }
  var sendToSession = "".concat(sendTo, " ").concat(sessionName);
  return {
    placeholder: sendToSession,
    ariaLabel: sendToSession
  };
};
var MessageInput = function MessageInput(props) {
  var _session$get, _session$get2;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var _useModal = useModal(),
    closeModal = _useModal.closeModal,
    openModal = _useModal.openModal;
  var _usePremadeAnswers = usePremadeAnswers(),
    substitutePlaceholders = _usePremadeAnswers.substitutePlaceholders;
  var chatSession = 'chatSession' in props ? props.chatSession : undefined;
  var threadMessageKey = 'threadMessageKey' in props ? props.threadMessageKey : null;
  var newSession = 'onSend' in props;
  var preventInitialFocus = 'preventInitialFocus' in props;
  var onInputResize = 'onInputResize' in props ? props.onInputResize : undefined;
  var totalNewChatMembers = 'totalNewChatMembers' in props ? props.totalNewChatMembers : 0;
  var videoDark = !!props.videoDark;
  var ownUserHasCapabilitySms = useAppSelector(function (state) {
    return selectOwnUserHasCapability(state, Capabilities.SMS);
  });
  var ownUserHasCapabilityGroupSms = useAppSelector(function (state) {
    return selectOwnUserHasCapability(state, Capabilities.GROUP_SMS);
  });
  var textAreaRef = useRef(null);
  var dispatch = useAppDispatch();
  var _useResizeObserver = useResizeObserver(),
    inputRef = _useResizeObserver.ref,
    inputRefHeight = _useResizeObserver.height;
  var ownExtensionKey = useAppSelector(selectOwnExtensionKey);
  var session = useAppSelector(function (state) {
    return state.chats.getIn(['sessions', chatSession]);
  });
  var extensions = useAppSelector(function (state) {
    return state.extensions.get('extensions');
  });
  var otherPartExtensionKey = (session === null || session === void 0 || (_session$get = session.get('members')) === null || _session$get === void 0 ? void 0 : _session$get.size) === 2 ? session.get('members').find(function (m) {
    return m.get('extensionKey') !== ownExtensionKey;
  }).get('extensionKey') : null;
  var otherPartExtension = useAppSelector(function (state) {
    return state.extensions.getIn(['extensions', otherPartExtensionKey]);
  });
  var clientKey = useAppSelector(function (state) {
    return state.chats.getIn(['client', 'key']);
  });
  var roomType = useAppSelector(function (state) {
    return state.chats.getIn(['sessions', chatSession, 'roomType'], 'session');
  });
  var composerStatePath = threadMessageKey ? ['composer', chatSession, threadMessageKey] : ['composer', chatSession];
  var attachment = useAppSelector(function (state) {
    return state.chats.getIn([].concat(composerStatePath, ['attachment']), null);
  });
  var messageText = useAppSelector(function (state) {
    return state.chats.getIn([].concat(composerStatePath, ['text']), '');
  });
  var replyToMessage = useAppSelector(function (state) {
    return state.chats.getIn(['composer', chatSession, 'quotedMessage']);
  });
  var defaultContent = {
    text: '',
    imageSrc: ''
  };
  var _useState = useState(defaultContent),
    _useState2 = _slicedToArray(_useState, 2),
    messageContent = _useState2[0],
    setMessageContent = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    cursorPosition = _useState4[0],
    setCursorPosition = _useState4[1];
  useEffect(function () {
    setMessageContent(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        text: messageText,
        attachment: attachment
      });
    });
    if (attachment) {
      setImageSource(attachment.file);
    }
    var textArea = textAreaRef === null || textAreaRef === void 0 ? void 0 : textAreaRef.current;
    if (!preventInitialFocus) {
      textArea === null || textArea === void 0 || textArea.focus();
    }
    return function () {
      dispatch(messageCompose(chatSession, !preventInitialFocus ? textArea === null || textArea === void 0 ? void 0 : textArea.value : '', threadMessageKey));
    };
  }, [chatSession, attachment, threadMessageKey]);
  useEffect(function () {
    textAreaRef.current.focus();
  }, [replyToMessage]);
  useEffect(function () {
    return onInputResize && onInputResize();
  }, [inputRefHeight]);
  useAutosizeTextArea(textAreaRef, messageContent.text);

  // Mention inserter and pre-made answer state is reset when we change chat.
  var _useState5 = useState(getInitialMentionInserterState()),
    _useState6 = _slicedToArray(_useState5, 2),
    mentionInserterState = _useState6[0],
    setMentionInserterState = _useState6[1];
  var _useState7 = useState(getInitialPremadeAnswerInserterState()),
    _useState8 = _slicedToArray(_useState7, 2),
    premadeAnswerInserterState = _useState8[0],
    setPremadeAnswerInserterState = _useState8[1];
  useEffect(function () {
    setMentionInserterState(getInitialMentionInserterState());
    setPremadeAnswerInserterState(getInitialPremadeAnswerInserterState());
    return closeModal();
  }, [chatSession]);
  var updateMessageAndPosition = function updateMessageAndPosition(text, index) {
    setMessageContent(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        text: text
      });
    });
    setCursorPosition(index);
  };
  useEffect(function () {
    if (cursorPosition !== null && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setCursorPosition(null);
    }
  }, [cursorPosition, messageContent.text]);
  var handleKeyDown = function handleKeyDown(event) {
    if (offerKeyEventToMessageInserter(event, mentionInserterState, setMentionInserterState, messageContent.text, updateMessageAndPosition) || offerKeyEventToPremadeAnswerInserter(event, premadeAnswerInserterState, setPremadeAnswerInserterState, updateMessageAndPosition, substitutePlaceholders)) {
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      handleSubmit(event);
    }
    if (isCmdOrCtrlPressed(event) && event.key === 'e' && event.shiftKey) {
      event.preventDefault();
      handleEmailSubmit();
    }
  };
  var handleSend = function handleSend() {
    var alsoAsSMS = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if ('onSend' in props) {
      props.onSend(messageContent.text, messageContent.attachment, alsoAsSMS);
      setMessageContent(defaultContent);
    } else {
      var _props$afterSend;
      var mentions = createMentionDTOsFromText(messageContent.text, extensions);
      if (alsoAsSMS) {
        dispatch(attachmentCompose(chatSession, null));
        dispatch(messageCompose(chatSession, ''));
      }
      if (attachment) {
        attachment.name = replaceWhitespaces(attachment.name, '_');
        dispatch(prepareAttachment(chatSession, clientKey, messageContent.text.trimEnd(), mentions, attachment, replyToMessage === null || replyToMessage === void 0 ? void 0 : replyToMessage.get('key'), threadMessageKey));
        dispatch(removeQuotedMessage(chatSession));
      } else {
        dispatch(sendChatMessage(chatSession, clientKey, messageContent.text.trimEnd(), mentions, null, alsoAsSMS, replyToMessage === null || replyToMessage === void 0 ? void 0 : replyToMessage.get('key'), threadMessageKey));
        dispatch(removeQuotedMessage(chatSession));
      }
      if (!alsoAsSMS) {
        dispatch(attachmentCompose(chatSession, null, threadMessageKey));
        dispatch(messageCompose(chatSession, '', threadMessageKey));
      }
      setMessageContent(defaultContent);
      (_props$afterSend = props.afterSend) === null || _props$afterSend === void 0 || _props$afterSend.call(props);
    }
  };
  var handleChatMessage = function handleChatMessage() {
    return handleSend(false);
  };
  var handleSmsMessage = function handleSmsMessage() {
    return handleSend(true);
  };
  var handleEmailSubmit = function handleEmailSubmit() {
    if (messageContent.text.trimEnd() && !messageContent.attachment) {
      handleEmailMessage();
    }
  };
  var handleEmailMessage = function handleEmailMessage() {
    if (hasEmailOption) {
      window.open("mailto:".concat(otherPartExtension.getIn(['contact', 'email']), "?&body=").concat(encodeURIComponent(messageContent.text.trimEnd())), '_blank');
      setMessageContent(defaultContent);
    }
  };
  var hasEmailOption = roomType === 'session' && (otherPartExtension === null || otherPartExtension === void 0 ? void 0 : otherPartExtension.getIn(['contact', 'email']));
  var setImageSource = function setImageSource(file) {
    if (getAttachmentTypeFromExtension(file.name) !== ChatAttachments.PHOTO) {
      setMessageContent(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          imageSrc: ''
        });
      });
    } else {
      var reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = function () {
        setMessageContent(function (prev) {
          if (reader.result) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              imageSrc: reader.result.toString()
            });
          }
          return _objectSpread(_objectSpread({}, prev), {}, {
            attachment: null,
            imageSrc: ''
          });
        });
      };
    }
  };
  var handleAddAttachment = function handleAddAttachment(file) {
    if (file) {
      if (file.size > MAX_ALLOWED_ATTACHMENT_SIZE) {
        openModal('messageAttachmentTooLarge');
        return;
      }
      if (chatSession) {
        dispatch(attachmentCompose(chatSession, {
          name: file.name,
          file: file
        }, threadMessageKey));
      }
      textAreaRef.current.focus();
    }
  };
  var handleRemoveAttachment = function handleRemoveAttachment() {
    if (chatSession) {
      dispatch(attachmentCompose(chatSession, null));
    }
    setMessageContent(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        attachment: undefined,
        imageSrc: ''
      });
    });
  };
  var handleDrop = function handleDrop(e) {
    e.stopPropagation();
    e.preventDefault();
    var file = e.dataTransfer.files[0];
    if (file) {
      handleAddAttachment(file);
    }
  };
  var handlePasteImage = function handlePasteImage(e) {
    var items = e.clipboardData.items;
    var file = null;
    for (var _i = 0, _Object$values = Object.values(items); _i < _Object$values.length; _i++) {
      var item = _Object$values[_i];
      if (item.type.indexOf('image') === 0) {
        file = item.getAsFile();
      }
    }
    if (file) {
      handleAddAttachment(file);
      e.preventDefault();
    }
  };
  var handleSubmit = function handleSubmit(event) {
    var _nativeEvent;
    event.preventDefault();
    if ((_nativeEvent = event.nativeEvent) !== null && _nativeEvent !== void 0 && _nativeEvent.isComposing) {
      return;
    }
    if (messageContent.text.trimEnd() || messageContent.attachment) {
      handleChatMessage();
    }
  };
  var handleTextChange = function handleTextChange(e) {
    var _e$target;
    var text = (_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.value;
    setMessageContent(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        text: text
      });
    });
    dispatch(messageCompose(chatSession, text, threadMessageKey));
    setMentionInserterState(getNextMentionInserterState(mentionInserterState, e.target));
    setPremadeAnswerInserterState(getNextPremadeAnswerInserterState(premadeAnswerInserterState, e.target));
  };
  var members = session === null || session === void 0 || (_session$get2 = session.get('members')) === null || _session$get2 === void 0 ? void 0 : _session$get2.filter(function (member) {
    return member.get('contact');
  });
  var memberExtensionKeys = members === null || members === void 0 ? void 0 : members.map(function (member) {
    return member.get('extensionKey');
  }).toSet();
  var filteredExtensions = extensions.filter(function (extension) {
    return memberExtensionKeys === null || memberExtensionKeys === void 0 ? void 0 : memberExtensionKeys.contains(extension.get('key'));
  });
  var hasSMS = function hasSMS() {
    if (replyToMessage || threadMessageKey) {
      return false;
    }
    if ([roomTypes["public"], roomTypes.room].includes(roomType)) {
      return ownUserHasCapabilityGroupSms;
    }
    if (roomType === roomTypes.session) {
      var _session$get3;
      if ((session === null || session === void 0 || (_session$get3 = session.get('members')) === null || _session$get3 === void 0 ? void 0 : _session$get3.size) > 2) {
        return ownUserHasCapabilityGroupSms;
      }
      if (totalNewChatMembers > 1) {
        return ownUserHasCapabilityGroupSms;
      }
      return ownUserHasCapabilitySms;
    }
    return false;
  };
  var _getComposerLabels = getComposerLabels(threadMessageKey, session === null || session === void 0 ? void 0 : session.get('name'), i18next),
    placeholder = _getComposerLabels.placeholder,
    ariaLabel = _getComposerLabels.ariaLabel;
  return /*#__PURE__*/_jsxs("div", {
    className: "relative w-full max-w-2xl",
    ref: inputRef,
    onDrop: handleDrop,
    children: [!threadMessageKey && /*#__PURE__*/_jsx(QuotedMessage, {
      chatSessionKey: session === null || session === void 0 ? void 0 : session.get('key'),
      videoDark: videoDark
    }), /*#__PURE__*/_jsx(PremadeAnswerInserter, {
      state: premadeAnswerInserterState,
      updateState: setPremadeAnswerInserterState,
      updateMessage: updateMessageAndPosition
    }), /*#__PURE__*/_jsx(PremadeAnswersManager, {
      message: messageContent.text,
      updateMessage: updateMessageAndPosition
    }), messageContent.attachment && /*#__PURE__*/_jsx(AttachmentPreview, {
      imageSrc: messageContent.imageSrc,
      fileName: messageContent.attachment.name,
      handleRemoveAttachment: handleRemoveAttachment
    }), /*#__PURE__*/_jsx("form", {
      onSubmit: handleSubmit,
      children: /*#__PURE__*/_jsxs("div", {
        className: twMerge('relative flex w-full items-start gap-x-1 p-2 first:rounded-t-sm last:rounded-b-sm sm:gap-x-3 sm:p-3', videoDark ? 'bg-gray-700' : 'bg-gray-200'),
        children: [/*#__PURE__*/_jsx(AddButton, {
          ref: textAreaRef,
          roomType: roomType,
          handleAddAttachment: handleAddAttachment,
          disablePremadeAnswers: !!threadMessageKey,
          chatSessionKey: chatSession,
          videoDark: videoDark
        }), /*#__PURE__*/_jsxs("div", {
          className: "relative flex h-full min-w-0 flex-1",
          children: [chatSession && /*#__PURE__*/_jsx(MentionInserter, {
            state: mentionInserterState,
            updateState: setMentionInserterState,
            message: messageContent.text,
            updateMessage: updateMessageAndPosition,
            sessionKey: chatSession
          }), /*#__PURE__*/_jsx("textarea", {
            id: "chat-message",
            "data-test-id": "message-input",
            className: twMerge('h-fit max-h-24 w-full min-w-0 flex-1 resize-none overflow-y-scroll bg-transparent pt-1 text-base text-wrap placeholder:truncate focus:border-transparent focus:ring-0 focus:outline-hidden', videoDark ? 'text-white placeholder:text-gray-400' : 'text-gray-800 placeholder:text-gray-500', threadMessageKey ? 'min-h-[32px]' : ''),
            onChange: handleTextChange,
            onPaste: handlePasteImage,
            onKeyDown: handleKeyDown,
            ref: textAreaRef,
            rows: 1,
            "aria-label": ariaLabel,
            value: messageContent.text,
            placeholder: placeholder
          })]
        }), /*#__PURE__*/_jsx(Suspense, {
          fallback: /*#__PURE__*/_jsx(_Fragment, {}),
          children: /*#__PURE__*/_jsx(EmojiSearch, {
            textAreaRef: textAreaRef,
            messageContent: messageContent,
            updateMessage: updateMessageAndPosition
          })
        }), /*#__PURE__*/_jsxs("div", {
          className: "flex items-center gap-x-1 sm:gap-x-2",
          children: [/*#__PURE__*/_jsx(Suspense, {
            fallback: /*#__PURE__*/_jsx(_Fragment, {}),
            children: /*#__PURE__*/_jsx(EmojiPicker, {
              textAreaRef: textAreaRef,
              messageContent: messageContent,
              updateMessage: updateMessageAndPosition,
              videoDark: videoDark
            })
          }), (session || newSession) && /*#__PURE__*/_jsx(SendButton, {
            message: messageContent,
            videoDark: videoDark,
            handleEmailMessage: hasEmailOption && !replyToMessage && !threadMessageKey ? handleEmailMessage : undefined,
            handleSend: filteredExtensions.size > 1 || totalNewChatMembers > 0 ? handleChatMessage : undefined,
            handleSmsMessage: hasSMS() ? handleSmsMessage : undefined
          })]
        })]
      })
    })]
  });
};
export default MessageInput;